// apps/web/src/features/commercial-proposals/public/usePublicProposal.ts
//
// @aiPurpose resolve the public proposal DTO for a token-driven public page
//   (#1199). Plain effect + AbortController, no react-query and no auth
//   coupling, so the public surface stays free of the authenticated
//   bootstrap/refresh machinery.
// @aiSecurity ANY thrown error (non-2xx, validation failure, abort race)
//   collapses to `notFound` so the page cannot leak whether the proposal
//   exists. A null/empty token is `notFound` immediately with NO network
//   call.
import { useEffect, useState } from 'react';
import type { CommercialProposalPublicDto } from '@magimp/shared';

import { resolvePublicProposal } from './publicApi.js';

export type PublicProposalState =
  | { status: 'loading' }
  | { status: 'ready'; dto: CommercialProposalPublicDto }
  | { status: 'notFound' };

export function usePublicProposal(token: string | null): PublicProposalState {
  const [state, setState] = useState<PublicProposalState>(
    token ? { status: 'loading' } : { status: 'notFound' },
  );

  useEffect(() => {
    if (!token) {
      setState({ status: 'notFound' });
      return;
    }
    let active = true;
    setState({ status: 'loading' });
    resolvePublicProposal(token)
      .then((dto) => {
        if (active) setState({ status: 'ready', dto });
      })
      .catch(() => {
        if (active) setState({ status: 'notFound' });
      });
    return () => {
      active = false;
    };
  }, [token]);

  return state;
}
